import {
  useCallback,
  useEffect,
  useState,
  useRef,
  useMemo,
} from 'preact/hooks';
import { useQuery } from '@tanstack/react-query';

const rulesetBaseURL = import.meta.env.VITE_DONATION_TRAIN_SERVICE_URI;

export function useRulesets(campaignID) {
  const [rulesets, setRulesets] = useState([]);

  const updateRuleset = useCallback((ruleset) => {
    const rulesetURL = `${rulesetBaseURL}/rulesets/${ruleset.id}`;

    fetch(rulesetURL, {
      method: 'PATCH',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(ruleset),
    })
      .then((res) => res.json())
      .then((res) => {
        setRulesets((existing) =>
          existing.map((e) => {
            if (ruleset.id === e.id && res) {
              return res;
            }

            return e;
          })
        );
      });
  }, []);

  const deleteRuleset = useCallback((ruleset) => {
    const rulesetURL = `${rulesetBaseURL}/rulesets/${ruleset.id}`;

    fetch(rulesetURL, {
      method: 'DELETE',
      headers: {
        'Content-Type': 'application/json',
      },
    })
      .then((res) => res.json())
      .then((res) => {
        setRulesets((existing) =>
          existing.filter((ruleset) => {
            return ruleset.id !== res.id;
          })
        );
      });
  }, []);

  const createRuleset = useCallback(
    (ruleset) => {
      const rulesetURL = `${rulesetBaseURL}/campaign-rulesets/${campaignID}`;

      fetch(rulesetURL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(ruleset),
      })
        .then((res) => res.json())
        .then((res) => {
          setRulesets((existing) => [res, ...existing]);
        });
    },
    [campaignID]
  );

  const { data: rulesetsForCampaign } = useQuery({
    queryKey: ['getRulesetsForCampaign', campaignID],
    queryFn: async () => {
      if (!campaignID || !campaignID.length) return null;

      const rulesetsURL = `${rulesetBaseURL}/campaign-rulesets/${campaignID}`;

      return await fetch(rulesetsURL, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      })
        .then((res) => res.json())
        .then((res) => res);
    },
    staleTime: 5000,
  });

  useEffect(() => {
    setRulesets(rulesetsForCampaign ?? []);
  }, [rulesetsForCampaign]);

  return {
    rulesets,
    updateRuleset,
    deleteRuleset,
    createRuleset,
  };
}

export const useRulesetModifiers = (ruleset) => {
  const initialRuleset = useRef(ruleset);
  const [contentChanged, setContentChanged] = useState(false);
  const [timer, updateTimer] = useState(ruleset?.timer ?? 5);
  const isNew = useMemo(() => ruleset.id === null, [ruleset.id]);

  const [donationValueMin, setDonationValueMin] = useState(
    ruleset?.donationValueMin ?? 0
  );

  const [donationsCountMin, setDonationsCountMin] = useState(
    ruleset?.donationsCountMin ?? 4
  );

  useEffect(() => {
    if (ruleset && initialRuleset.current !== ruleset) {
      initialRuleset.current = ruleset;
      setDonationValueMin(ruleset.donationValueMin);
      setDonationsCountMin(ruleset.donationsCountMin);
      updateTimer(ruleset.timer);
      setContentChanged(false);
    }
  }, [ruleset]);

  const differsFromOriginal = useCallback((key, value) => {
    return initialRuleset.current[key] !== value;
  }, []);

  const handleMinChange = useCallback(
    (event) => {
      const newValue = event.target.valueAsNumber;
      if (newValue !== donationValueMin) {
        setContentChanged(differsFromOriginal('donationValueMin', newValue));
        setDonationValueMin(newValue);
      }
    },
    [differsFromOriginal, donationValueMin]
  );

  const handleCountChange = useCallback(
    (event) => {
      const newValue = event.target.valueAsNumber;
      if (newValue !== donationsCountMin) {
        setContentChanged(differsFromOriginal('donationsCountMin', newValue));
        setDonationsCountMin(newValue);
      }
    },
    [differsFromOriginal, donationsCountMin]
  );

  const handleTimerChange = useCallback(
    (event) => {
      const newValue = event.target.valueAsNumber;
      if (newValue !== timer) {
        setContentChanged(differsFromOriginal('timer', newValue));
        updateTimer(newValue);
      }
    },
    [differsFromOriginal, timer]
  );

  return {
    isNew,
    handleTimerChange,
    handleCountChange,
    handleMinChange,
    contentChanged,
    timer,
    donationValueMin,
    donationsCountMin,
  };
};
